import type { Navigation } from 'react-router-dom';

/**
 * Cuánto debe durar una navegación GET antes de que merezca enseñar el Loader.
 *
 * Por debajo de este umbral no se pinta nada: en Chrome de escritorio casi
 * toda la navegación resuelve en menos, así que el usuario no ve un parpadeo
 * del cuadrado granate en cada clic. Por encima —una tablet en carretera— se
 * ve siempre.
 *
 * Es el único sitio donde vive el número: se ajusta aquí y lo heredan los 7
 * layouts.
 */
export const NAVIGATION_FEEDBACK_DELAY_MS = 300;

export type NavigationFeedbackMode = 'none' | 'immediate' | 'delayed';

/**
 * Clasifica una navegación del router según el feedback que merece.
 *
 * `immediate` reproduce byte a byte la condición que tenían los layouts
 * (`state === 'submitting' || (state === 'loading' && formMethod != null)`):
 * un `submitting` siempre lleva `formMethod`, así que «no está parado y tiene
 * método de formulario» es la misma frase dicha más corta.
 *
 * `delayed` es lo nuevo: la navegación GET entre pasos, que hasta ahora no
 * pintaba nada.
 */
export const classifyNavigationFeedback = (
  navigation: Pick<Navigation, 'state' | 'formMethod'>
): NavigationFeedbackMode => {
  if (navigation.state === 'idle') return 'none';
  if (navigation.formMethod != null) return 'immediate';
  return 'delayed';
};

// ═══════════════════════════════════════════════════════════════════════
// Trabajo PREVIO a la navegación
// ═══════════════════════════════════════════════════════════════════════
// El velo se apoya en `useNavigation`, que solo sabe de navegaciones ya
// empezadas. Pero once handlers de la aplicación consultan al servidor ANTES
// de llamar a `navigate()` — el patrón típico de las tablas:
//
//   const { data } = await client.query({ query: GET_ORDER_BY_ID, … });
//   dispatch(roidOTSelected(…));      // las migas necesitan el ROID
//   navigate(`/work-orders/accidents/${id}`);
//
// Durante ese `await` el router sigue en `idle`: no hay navegación que cubrir
// y la pantalla se queda muerta. Cazado en tablet (2026-08-15) al abrir un
// accidente con mucha información desde la tabla, mientras que en la tabla de
// actuaciones —que navega sin esperar— el velo salía perfectamente.
//
// Este contador deja que esos handlers declaren «estoy trabajando para
// navegar». Es un contador y no un booleano porque dos handlers pueden
// solaparse (doble toque, o una tabla que refresca mientras navegas).

let pendingCount = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** Suscripción para `useSyncExternalStore`. */
export const subscribePendingWork = (onChange: () => void): (() => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

export const hasPendingWork = (): boolean => pendingCount > 0;

/**
 * Envuelve el trabajo que ocurre ANTES de navegar para que el velo lo cubra.
 *
 *   const abrir = useCallback(
 *     () => withNavigationFeedback(async () => {
 *       const { data } = await client.query({ … });
 *       navigate(`/ruta/${data.id}`);
 *     }),
 *     [deps],
 *   );
 *
 * El contador se libera en el siguiente tick, no al terminar `fn`: `navigate()`
 * es lo último que hace el handler y el router tarda un instante en pasar a
 * `loading`. Sin ese puente el velo parpadearía entre las dos fases.
 */
export const withNavigationFeedback = async <T>(
  fn: () => Promise<T>
): Promise<T> => {
  pendingCount += 1;
  notify();
  try {
    return await fn();
  } finally {
    setTimeout(() => {
      pendingCount -= 1;
      notify();
    }, 0);
  }
};
